import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { HexColorPicker } from 'react-colorful';
import { spacing, fontWeight } from '../../styles/theme';
import BackButton from '../components/BackButton';
import WebvonkLoader from '../components/WebvonkLoader';
import { BANNER_ICONS } from '../components/AppBanner';

const WEBVONK_URL = 'https://swxfaqoftmwphbparotf.supabase.co';
const WEBVONK_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InN3eGZhcW9mdG13cGhicGFyb3RmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzk1MjY1OTQsImV4cCI6MjA5NTEwMjU5NH0.IZZAiD-ndE_CvkuY3E44N8Sm7u1EXnsYaYnCda6R-v4';
const getAuthToken = () => { try { return JSON.parse(localStorage.getItem('kmfc_user') || '{}')?.access_token; } catch { return null; } };

const BANNER_ID = 'ddc190c7-d1b5-4650-97e9-19686cd4dd1b';

const slideDown = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

const Toast = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  padding: 10px 18px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  animation: ${slideDown} 0.25s ease;
  background: ${props => props.$type === 'error' ? '#ef4444' : '#16a34a'};
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
`;

const ROLES = [
  { id: 1, label: 'Ontwikkelaar' },
  { id: 2, label: 'Eigenaar' },
  { id: 3, label: 'Medewerker' },
];

const Container = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Content = styled.div`
  padding: 80px ${spacing.lg} ${spacing.xl};
  max-width: 600px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 80px ${spacing.md} ${spacing.xl};
  }
`;

const Header = styled.div`
  padding-bottom: ${spacing.lg};
  h1 {
    font-size: 28px;
    font-weight: ${fontWeight.bold};
    color: ${props => props.theme.colors.text};
    margin: 0;
    letter-spacing: -0.02em;
  }
`;

const Section = styled.div`margin-bottom: 24px;`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 8px;
  padding-left: 4px;
  opacity: 0.6;
`;

const Card = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 14px;
  overflow: ${props => props.$overflow || 'hidden'};
  border: 1px solid rgba(255,255,255,0.06);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  &:not(:last-child) { border-bottom: 1px solid rgba(255,255,255,0.05); }
`;

const RowLabel = styled.div`
  font-size: 15px;
  font-weight: ${fontWeight.medium};
  color: ${props => props.theme.colors.text};
  letter-spacing: -0.01em;
`;

const RowSub = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  margin-top: 2px;
  opacity: 0.5;
`;

const Toggle = styled.button`
  width: 48px;
  height: 28px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  background: ${props => props.$on ? '#22c55e' : 'rgba(255,255,255,0.1)'};
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => props.$on ? '23px' : '3px'};
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s ease;
    box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  }
`;

const FieldRow = styled.div`
  padding: 14px 16px;
  &:not(:last-child) { border-bottom: 1px solid rgba(255,255,255,0.05); }
`;

const FieldLabel = styled.div`
  font-size: 12px;
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
  opacity: 0.5;
`;

const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  padding: 10px 12px;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: rgba(255,255,255,0.2); }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ColorSwatch = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.$color || 'rgba(255,255,255,0.08)'};
  border: 1.5px solid rgba(255,255,255,0.15);
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.15s;
  &:hover { transform: scale(1.08); }
`;

const ColorPickerPopover = styled.div`
  position: absolute;
  z-index: 1000;
  top: calc(100% + 8px);
  left: 0;
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.6);

  .react-colorful {
    width: 220px;
    height: 180px;
    border-radius: 8px;
  }
  .react-colorful__saturation {
    border-radius: 8px 8px 0 0;
  }
  .react-colorful__hue {
    border-radius: 0 0 8px 8px;
    height: 16px;
    margin-top: 10px;
  }
  .react-colorful__pointer {
    width: 20px;
    height: 20px;
    border: 2px solid #fff;
  }
`;

const ColorPickerWrap = styled.div`
  position: relative;
`;

const PresetRow = styled.div`
  display: flex;
  gap: 7px;
  margin-top: 10px;
`;

const PresetSwatch = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${props => props.$color};
  border: 2px solid ${props => props.$active ? '#fff' : 'transparent'};
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.15s;
  box-shadow: 0 0 0 1px rgba(255,255,255,0.15);
  &:hover { transform: scale(1.2); }
`;

// Icon picker
const IconGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 16px;
`;

const IconChip = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 100px;
  border: 1.5px solid ${props => props.$active ? '#f97316' : 'rgba(255,255,255,0.08)'};
  background: ${props => props.$active ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.04)'};
  color: ${props => props.$active ? '#f97316' : 'rgba(255,255,255,0.6)'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: rgba(249,115,22,0.5); color: #f97316; }
`;

// Role chips
const RoleGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 16px;
`;

const RoleChip = styled.button`
  padding: 7px 14px;
  border-radius: 100px;
  border: 1.5px solid ${props => props.$active ? '#3b82f6' : 'rgba(255,255,255,0.08)'};
  background: ${props => props.$active ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)'};
  color: ${props => props.$active ? '#3b82f6' : 'rgba(255,255,255,0.6)'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: rgba(59,130,246,0.5); color: #3b82f6; }
`;

const InlineError = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(239,68,68,0.15);
  border: 1px solid rgba(239,68,68,0.35);
  color: #ef4444;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 100px;
  margin: 0 16px 12px;
`;

// ─── Preview ──────────────────────────────────────────────────────────────────

const PreviewWrap = styled.div`
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.06);
  background: ${props => props.theme.colors.surface};
`;

const PreviewBannerBar = styled.div`
  background: ${props => props.$color || '#ea7211'};
  color: #fff;
  padding: 10px 40px 11px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  letter-spacing: 0.1px;
`;

const PreviewDismiss = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #fff;
`;

const PreviewBtn = styled.span`
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.35);
  border-radius: 100px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
`;

const PreviewBlock = styled.div`
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
`;

const PreviewBlockIcon = styled.div`
  color: #fff;
  opacity: 0.9;
`;

const PreviewBlockTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  letter-spacing: -0.02em;
`;

const PreviewBlockMsg = styled.div`
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
  opacity: 0.6;
`;

const PreviewCode = styled.div`
  margin-top: 4px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 18px;
  letter-spacing: 8px;
  color: rgba(255,255,255,0.2);
  font-family: monospace;
`;

const SaveBtn = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  background: #f97316;
  color: #fff;
  font-size: 15px;
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  transition: filter 0.15s;
  letter-spacing: -0.01em;
  &:hover { filter: brightness(1.1); }
  &:disabled { opacity: 0.5; cursor: not-allowed; filter: none; }
`;

const StatusText = styled.div`
  text-align: center;
  font-size: 13px;
  margin-top: 10px;
  color: ${props => props.$error ? '#ef4444' : '#22c55e'};
`;


const isValidHex = (c) => /^#[0-9a-fA-F]{3,6}$/.test(c);

export default function BannerScreen() {
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState('');
  const [color, setColor] = useState('');
  const [dismissable, setDismissable] = useState(true);
  const [blockScreen, setBlockScreen] = useState(false);
  const [icon, setIcon] = useState('geen');
  const [roles, setRoles] = useState([1, 2, 3]);
  const [buttonLabel, setButtonLabel] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const load = async () => {
      const res = await fetch(`${WEBVONK_URL}/rest/v1/banners?id=eq.${BANNER_ID}&select=*`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'apikey': WEBVONK_ANON_KEY },
      });
      const rows = await res.json();
      const data = rows?.[0];
      if (data) {
        setActive(data.active ?? false);
        setMessage(data.message ?? '');
        setColor(data.color ?? '');
        setDismissable(data.dismissable ?? true);
        setBlockScreen(data.block_screen ?? false);
        setIcon(data.icon && data.icon in BANNER_ICONS ? data.icon : 'geen');
        setRoles(data.roles?.length ? data.roles : [1, 2, 3]);
        setButtonLabel(data.button_label ?? '');
        setButtonUrl(data.button_url ?? '');
        if (data.expires_at) {
          const d = new Date(data.expires_at);
          const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
          setExpiresAt(local);
        } else {
          setExpiresAt('');
        }
      }
      setLoading(false);
    };
    load();
  }, []);

  const toggleRole = (id) => {
    setRoles(prev => {
      if (prev.includes(id) && prev.length === 1) {
        showToast('Minimaal 1 rol vereist', 'error');
        return prev;
      }
      return prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id];
    });
  };

  const save = async () => {
    if (!message.trim()) { showToast('Vul een bericht in', 'error'); return; }
    if (!isValidHex(color)) { showToast('Vul een geldige kleur in, bijv. #ea7211', 'error'); return; }
    if (!icon || !(icon in BANNER_ICONS)) { showToast('Kies een icon', 'error'); return; }
    setSaving(true);
    const res = await fetch(`${WEBVONK_URL}/rest/v1/rpc/update_banner`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'apikey': WEBVONK_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        p_id: BANNER_ID,
        p_active: active,
        p_message: message,
        p_color: color || null,
        p_dismissable: dismissable,
        p_block_screen: blockScreen,
        p_icon: icon,
        p_roles: roles.length ? roles : null,
        p_button_label: buttonLabel || null,
        p_button_url: buttonUrl || null,
        p_expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      }),
    });
    setSaving(false);
    if (res.ok) {
      showToast('Opgeslagen');
    } else {
      const err = await res.text();
      showToast(`Fout ${res.status}: ${err}`, 'error');
    }
  };

  if (loading) return <WebvonkLoader fullscreen text="Banner wordt geladen..." />;

  return (
    <Container>

      <Content>
        <Header><h1>Banner</h1></Header>

        <Section>
          <SectionTitle>Voorbeeld</SectionTitle>
          <PreviewWrap>
            {blockScreen ? (
              <PreviewBlock>
                {BANNER_ICONS[icon]?.icon && (
                  <PreviewBlockIcon>
                    {React.cloneElement(BANNER_ICONS[icon].icon, { size: 32 })}
                  </PreviewBlockIcon>
                )}
                <PreviewBlockTitle>{message || 'Tijdelijk niet beschikbaar'}</PreviewBlockTitle>
                <PreviewBlockMsg>{message ? '' : 'Vul een bericht in'}</PreviewBlockMsg>
                <PreviewCode>······</PreviewCode>
              </PreviewBlock>
            ) : (
              <PreviewBannerBar $color={isValidHex(color) ? color : '#ea7211'}>
                {BANNER_ICONS[icon]?.preview && React.cloneElement(BANNER_ICONS[icon].preview, { size: 13, style: { flexShrink: 0 } })}
                <span>{message || 'Jouw bericht verschijnt hier'}</span>
                {buttonLabel && <PreviewBtn>{buttonLabel}</PreviewBtn>}
                {dismissable && <PreviewDismiss>✕</PreviewDismiss>}
              </PreviewBannerBar>
            )}
          </PreviewWrap>
        </Section>


        <Section>
          <SectionTitle>Status</SectionTitle>
          <Card>
            <Row>
              <div>
                <RowLabel>Actief</RowLabel>
                <RowSub>Toon de banner aan gebruikers</RowSub>
              </div>
              <Toggle $on={active} onClick={() => setActive(v => !v)} />
            </Row>
            <Row>
              <div>
                <RowLabel>Wegklikbaar</RowLabel>
                <RowSub>Gebruiker kan banner sluiten</RowSub>
              </div>
              <Toggle $on={dismissable} onClick={() => setDismissable(v => !v)} />
            </Row>
            <Row>
              <div>
                <RowLabel>Scherm blokkeren</RowLabel>
                <RowSub>Toont een volledig scherm melding</RowSub>
              </div>
              <Toggle $on={blockScreen} onClick={() => setBlockScreen(v => !v)} />
            </Row>
          </Card>
        </Section>

        <Section>
          <SectionTitle>Zichtbaar voor</SectionTitle>
          <Card>
            <RoleGrid>
              {ROLES.map(r => (
                <RoleChip
                  key={r.id}
                  $active={roles.includes(r.id)}
                  onClick={() => toggleRole(r.id)}
                >
                  {r.label}
                </RoleChip>
              ))}
            </RoleGrid>
          </Card>
        </Section>

        <Section>
          <SectionTitle>Icon</SectionTitle>
          <Card>
            <IconGrid>
              {Object.entries(BANNER_ICONS).map(([key, val]) => (
                <IconChip key={key} $active={icon === key} onClick={() => setIcon(key)}>
                  {val.preview}
                  {val.label}
                </IconChip>
              ))}
            </IconGrid>
          </Card>
        </Section>

        <Section>
          <SectionTitle>Inhoud</SectionTitle>
          <Card $overflow="visible">
            <FieldRow>
              <FieldLabel>Bericht</FieldLabel>
              <TextInput
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Bericht voor gebruikers..."
              />
            </FieldRow>
            <FieldRow>
              <FieldLabel>Kleur</FieldLabel>
              <ColorRow>
                <ColorPickerWrap ref={pickerRef}>
                  <ColorSwatch
                    $color={isValidHex(color) ? color : null}
                    onClick={() => setShowPicker(v => !v)}
                  />
                  {showPicker && (
                    <ColorPickerPopover>
                      <HexColorPicker
                        color={isValidHex(color) ? color : '#ea7211'}
                        onChange={setColor}
                      />
                      <PresetRow>
                        {['#ea7211', '#ef4444', '#3b82f6', '#22c55e', '#8b5cf6', '#f59e0b', '#000000', '#ffffff'].map(p => (
                          <PresetSwatch
                            key={p}
                            $color={p}
                            $active={color === p}
                            onClick={() => { setColor(p); }}
                          />
                        ))}
                      </PresetRow>
                    </ColorPickerPopover>
                  )}
                </ColorPickerWrap>
                <TextInput
                  value={color}
                  onChange={e => setColor(e.target.value)}
                  placeholder="#ea7211"
                />
              </ColorRow>
            </FieldRow>
          </Card>
        </Section>

        <Section>
          <SectionTitle>Vervaldatum (optioneel)</SectionTitle>
          <Card>
            <FieldRow>
              <FieldLabel>Verberg banner na</FieldLabel>
              <TextInput
                type="datetime-local"
                value={expiresAt}
                onChange={e => setExpiresAt(e.target.value)}
              />
              {expiresAt && (
                <div style={{ marginTop: 8 }}>
                  <button onClick={() => setExpiresAt('')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: 12, cursor: 'pointer', padding: 0 }}>
                    Datum wissen
                  </button>
                </div>
              )}
            </FieldRow>
          </Card>
        </Section>

        <Section>
          <SectionTitle>Knop (optioneel)</SectionTitle>
          <Card>
            <FieldRow>
              <FieldLabel>Label</FieldLabel>
              <TextInput
                value={buttonLabel}
                onChange={e => setButtonLabel(e.target.value)}
                placeholder="Meer info"
              />
            </FieldRow>
            <FieldRow>
              <FieldLabel>URL</FieldLabel>
              <TextInput
                value={buttonUrl}
                onChange={e => setButtonUrl(e.target.value)}
                placeholder="https://..."
              />
            </FieldRow>
          </Card>
        </Section>

        <SaveBtn onClick={save} disabled={saving}>
          {saving ? 'Opslaan...' : 'Opslaan'}
        </SaveBtn>
        {toast && ReactDOM.createPortal(
          <Toast $type={toast.type}>{toast.msg}</Toast>,
          document.body
        )}
      </Content>
    </Container>
  );
}
